import React, { useEffect } from 'react'
import { Alert, Button, Form, Input, Typography } from 'antd'
import ConceptualLabelSelectDisciplines from '../select/ConceptualLabelSelectDisciplines'
import ConceptualLabelSelectParent from '../select/ConceptualLabelSelectParent'
import ConceptualLabelSelectType from '../select/ConceptualLabelSelectType'
import { useFetchDisciplinesQuery } from '../../../../store/apis/discipline.api'
import { useFetchConceptualLabelsQuery } from '../../../../store/apis/conceptual-label.api'
import { ConceptualLabelScopes } from '../../../../constants/conceptual-label-scopes'
import { CONCEPTUAL_LABEL_NAME_LIMIT } from '../../../../constants/input-limits'

const { TextArea } = Input
const { Text } = Typography

export const FORM_FIELDS = {
    NAME: 'name',
    TYPE: 'typeId',
    DESCRIPTION: 'description',
    SOURCE: 'source',
    DISCIPLINE: 'disciplineId',
    PARENT: 'parentId',
}

const ConceptualLabelForm = ({
    onSubmit,
    initialValues,
    error,
    loading,
    formResetTrigger,
    submitButtonText = 'Submit',
    readonlyFields = [],
}) => {
    const [form] = Form.useForm()

    const { data: disciplines } = useFetchDisciplinesQuery({ scope: ConceptualLabelScopes.ALL })
    const { data: conceptualLabels } = useFetchConceptualLabelsQuery()

    const disciplineId = Form.useWatch(FORM_FIELDS.DISCIPLINE, form)
    const parentId = Form.useWatch(FORM_FIELDS.PARENT, form)

    const getDisciplineName = disciplineId => {
        if (!disciplineId || !disciplines) return null
        const discipline = disciplines.find(d => d.id === disciplineId)
        return discipline ? `${discipline.visualId} - ${discipline.name}` : `ID: ${disciplineId}`
    }

    const getParentLabelName = parentId => {
        if (!parentId || !conceptualLabels) return null
        const parent = conceptualLabels.find(cl => cl.id === parentId)
        return parent ? `${parent.visualId} - ${parent.name}` : `ID: ${parentId}`
    }

    useEffect(() => {
        form.resetFields()
    }, [form, formResetTrigger])

    const isFieldReadonly = fieldName => readonlyFields.includes(fieldName)
    const allFieldsReadonly = readonlyFields.length === Object.keys(FORM_FIELDS).length

    const handleDisciplineChange = () => {
        if (!isFieldReadonly(FORM_FIELDS.PARENT) && parentId) form.setFieldValue(FORM_FIELDS.PARENT, undefined)
    }

    return (
        <Form
            form={form}
            name='conceptual-label-form'
            autoComplete='off'
            onFinish={onSubmit}
            requiredMark={false}
            layout='vertical'
        >
            <Form.Item
                name={FORM_FIELDS.NAME}
                label='Name'
                initialValue={initialValues?.name}
                rules={[
                    { required: true, message: 'Please enter the name' },
                    { max: CONCEPTUAL_LABEL_NAME_LIMIT, message: `Conceptual label name cannot exceed ${CONCEPTUAL_LABEL_NAME_LIMIT} characters` }
                ]}
            >
                {isFieldReadonly(FORM_FIELDS.NAME) ? (
                    <Text>{initialValues?.name || 'No name'}</Text>
                ) : (
                    <Input placeholder='Enter name' maxLength={CONCEPTUAL_LABEL_NAME_LIMIT} showCount/>
                )}
            </Form.Item>
            <Form.Item
                name={FORM_FIELDS.TYPE}
                label='Type'
                initialValue={initialValues?.typeId}
                rules={[{ required: true, message: 'Please select a type' }]}
            >
                {/* {isFieldReadonly(FORM_FIELDS.TYPE) ? (
                    <Text>{getTypeName(initialValues?.typeId) || 'No type selected'}</Text>
                ) : ( */}
                <ConceptualLabelSelectType />
                {/* )} */}
            </Form.Item>
            <Form.Item name={FORM_FIELDS.DESCRIPTION} label='Description' initialValue={initialValues?.description}>
                {isFieldReadonly(FORM_FIELDS.DESCRIPTION) ? (
                    <Text>{initialValues?.description || 'No description'}</Text>
                ) : (
                    <TextArea rows={4} placeholder='Enter description' />
                )}
            </Form.Item>
            <Form.Item name={FORM_FIELDS.SOURCE} label='Source' initialValue={initialValues?.source}>
                {isFieldReadonly(FORM_FIELDS.SOURCE) ? (
                    <Text>{initialValues?.source || 'No source'}</Text>
                ) : (
                    <Input placeholder='Enter source' />
                )}
            </Form.Item>
            <Form.Item
                name={FORM_FIELDS.DISCIPLINE}
                label='Discipline'
                initialValue={initialValues?.disciplineId}
                rules={[{ required: true, message: 'Please select a discipline' }]}
            >
                {isFieldReadonly(FORM_FIELDS.DISCIPLINE) ? (
                    <Text>{getDisciplineName(initialValues?.disciplineId) || 'No discipline selected'}</Text>
                ) : (
                    <ConceptualLabelSelectDisciplines onChange={handleDisciplineChange} />
                )}
            </Form.Item>
            <Form.Item name={FORM_FIELDS.PARENT} label='Parent' initialValue={initialValues?.parentId}>
                {isFieldReadonly(FORM_FIELDS.PARENT) ? (
                    <Text>{initialValues?.parentId ? getParentLabelName(initialValues.parentId) : 'No parent'}</Text>
                ) : (
                    <ConceptualLabelSelectParent disciplineId={disciplineId} />
                )}
            </Form.Item>

            {error && <Alert message={error} type='error' showIcon closable />}
            {!allFieldsReadonly && (
                <Form.Item>
                    <Button type='primary' htmlType='submit' block loading={loading}>
                        {submitButtonText}
                    </Button>
                </Form.Item>
            )}
        </Form>
    )
}

export default ConceptualLabelForm
